// Front-end Procore helper. Calls our server function /api/procoreBudget,
// which holds the Procore credentials server-side and returns the current
// Labor budget lines for a project's "Hardaway View REV 1" budget view.
//
// Returns: { view, projectId, pulledAt, count, lines: [{ code, description,
//   costType, revisedBudget, forecastCostToComplete, jobToDateCosts }] }

// token = the MSAL id_token (useData().getToken()); the function rejects callers
// without a verified finance/admin sign-in.
export async function fetchProcoreBudget(procoreProjectId, token) {
  if (!procoreProjectId) throw new Error("This job has no Procore Project ID set (add it in Admin).");
  const res = await fetch(`/api/procoreBudget?projectId=${encodeURIComponent(procoreProjectId)}`, {
    headers: token ? { "X-Delphi-Token": token } : {},
  });
  const text = await res.text();
  let data;
  try { data = JSON.parse(text); } catch { throw new Error(`Procore pull failed: ${text.slice(0, 300)}`); }
  if (!res.ok) throw new Error(data.error || data.message || `Procore pull failed (HTTP ${res.status}).`);
  return data;
}

// Build the SharePoint rows for one snapshot from the pulled lines.
// All rows share a SnapshotId + SnapshotDate so history charts cleanly.
export function buildSnapshotRows({ jobNumber, lines, pulledBy }) {
  const now = new Date();
  const snapshotId = now.toISOString();
  return (lines || []).map((l) => ({
    // Title is the list's built-in column (renamed to ProjectJobNumber) — keep it
    // to JUST the job number so the list can be grouped/filtered by project.
    Title: jobNumber,
    ProjectJobNumber: jobNumber,
    SnapshotId: snapshotId,
    SnapshotDate: now.toISOString(),
    BudgetCode: l.code,
    Description: l.description,
    OriginalBudget: l.originalBudget,
    RevisedBudget: l.revisedBudget,
    ForecastCostToComplete: l.forecastCostToComplete,
    JobToDateCosts: l.jobToDateCosts,
    PulledBy: pulledBy || "",
  }));
}

// Group flat snapshot rows by SnapshotId → [{ id, date, pulledBy, lines, totals }]
export function groupSnapshots(rows) {
  const by = new Map();
  (rows || []).forEach((r) => {
    const id = r.SnapshotId || r.SnapshotDate || "?";
    if (!by.has(id)) by.set(id, { id, date: r.SnapshotDate || id, pulledBy: r.PulledBy || "", lines: [] });
    by.get(id).lines.push(r);
  });
  const snaps = Array.from(by.values()).map((s) => {
    const sum = (k) => s.lines.reduce((a, l) => a + (Number(l[k]) || 0), 0);
    const original = sum("OriginalBudget");
    const revised = sum("RevisedBudget");
    const forecast = sum("ForecastCostToComplete");
    const jtd = sum("JobToDateCosts");
    return { ...s, totals: { original, revised, forecast, jtd } };
  });
  snaps.sort((a, b) => new Date(a.date) - new Date(b.date));
  return snaps;
}
